import { ArrowLeft, Heart, Users, Target, Lightbulb, Mail, MapPin, Phone } from 'lucide-react';
import { useEffect } from 'react';
import { PageShell } from './layout/PageShell';

interface AboutPageProps {
  onBack: () => void;
}

export function AboutPage({ onBack }: AboutPageProps) {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50">
      {/* Content - scroll handled by main / App wrapper */}
      <PageShell className="pt-page-safe-sm pb-android-pwa-doc xl:max-w-4xl xl:mx-auto xl:px-4 xl:px-8 xl:pt-5 xl:pb-24">
          {/* Content container - fullscreen on mobile, rounded modal on desktop */}
          <div className="pt-5 xl:bg-white xl:rounded-3xl xl:shadow-xl xl:border-2 xl:border-purple-100 xl:p-8">
            {/* Back button */}
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 px-4 xl:px-0 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>

            {/* Header */}
            <h1 className="text-3xl sm:text-4xl mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 px-4 xl:px-0">
              Om Talentfunn
            </h1>

            {/* Content */}
            <div className="space-y-6 px-4 xl:px-0">
              
              {/* About Talentfunn */}
              <section>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Talentfunn er en moderne plattform som kobler sammen talenter med arbeidsgivere på en enkel og effektiv måte. 
                  Vår visjon er å gjøre det enkelt for bedrifter å finne de rette kandidatene, og for talenter å vise frem sine ferdigheter.
                </p>
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 pt-2">
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-3">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">For talenter</h3>
                    <p className="text-sm text-gray-600">Vis frem dine ferdigheter og bli oppdaget av arbeidsgivere</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-3">
                      <Target className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">For arbeidsgivere</h3>
                    <p className="text-sm text-gray-600">Finn kvalifiserte kandidater raskt og enkelt</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mb-3">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">Trygt og sikkert</h3>
                    <p className="text-sm text-gray-600">Vi tar personvern og datasikkerhet på alvor</p>
                  </div>
                </div>
              </section>

              {/* Company Info */}
              <section>
                <h2 className="text-2xl text-purple-900 mb-3">Evensen Solutions</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Talentfunn er utviklet og driftet av Evensen Solutions, et norsk teknologiselskap som spesialiserer seg 
                  på innovative digitale løsninger.
                </p>
                
                {/* Organization Number */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-200 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Organisasjonsnummer</p>
                      <p className="text-lg font-semibold text-gray-900">933 184 048</p>
                    </div>
                  </div>
                </div>

                {/* Contact Info – Vipps compliance: godt synlig firma- og kontaktinformasjon */}
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900">Kontakt oss</h3>
                  
                  <div className="flex items-start gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span>Blåknappveien 9, 4823 Nedenes</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <a href="tel:94146523" className="hover:text-purple-600 transition-colors">
                      94 14 65 23
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <a 
                      href="mailto:kontakt@talentfunn.no" 
                      className="hover:text-purple-600 transition-colors"
                    >
                      kontakt@talentfunn.no
                    </a>
                  </div>
                </div>
              </section>

              {/* Version & Credits */}
              <section className="border-t-2 border-purple-100 pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                    <span className="text-sm">Laget med kjærlighet i Norge</span>
                  </div>
                  <div className="text-center text-sm text-gray-500">
                    Talentfunn © {new Date().getFullYear()} Evensen Solutions
                  </div>
                  <div className="text-center text-xs text-gray-400">
                    Versjon 1.0.0
                  </div>
                </div>
              </section>

            </div>
          </div>
      </PageShell>
    </div>
  );
}